'use client'

import { useLocale, useTranslations } from 'next-intl'
import { type ReactNode, useCallback } from 'react'

import {
  LeaderboardBoard,
  LeaderboardLoading
} from '@/features/leaderboard/components/LeaderboardBoard'
import { LeaderboardLoadError } from '@/features/leaderboard/components/LeaderboardLoadError'
import { usePagedLeaderboard } from '@/features/leaderboard/usePagedLeaderboard'

import { fetchAthletesLeaderboardPage } from '../api'
import type { AthletesLeaderboardScope } from '../types'
import { AthleteRow, AthleteRowHeader } from './AthleteRow'

type AthletesBoardProps = {
  scope: AthletesLeaderboardScope
  emptyState: ReactNode
}

/** The athletes board (US-ATH-18.1): pages of 20, «Load more». */
export function AthletesBoard({ scope, emptyState }: AthletesBoardProps) {
  const t = useTranslations('leaderboardBoard')
  const locale = useLocale()
  const sports = scope.sportTypes.join(',')
  const countries = scope.countries.join(',')
  const fetchPage = useCallback(
    (page: number, pageSize: number) =>
      fetchAthletesLeaderboardPage(
        {
          sportTypes: sports ? sports.split(',') : [],
          countries: countries ? countries.split(',') : [],
          period: scope.period,
          search: scope.search
        },
        page,
        pageSize,
        locale
      ),
    [sports, countries, scope.period, scope.search, locale]
  )

  const board = usePagedLeaderboard({
    queryKey: [
      'athletes_leaderboard_page',
      sports,
      countries,
      scope.period,
      scope.search ?? '',
      locale
    ],
    fetchPage
  })

  if (board.isLoading) {
    return <LeaderboardLoading />
  }
  if (board.isError) {
    return <LeaderboardLoadError onRetry={board.retry} />
  }
  if (board.blocks.length === 0) {
    return emptyState
  }

  return (
    <LeaderboardBoard
      header={<AthleteRowHeader />}
      blocks={board.blocks}
      getRowKey={row => row.athleteId}
      renderRow={row => <AthleteRow row={row} />}
      selfLabel={t('yourPosition')}
      hasMore={board.hasMore}
      isFetchingMore={board.isFetchingMore}
      onLoadMore={board.loadMore}
      onLoadPrevious={board.loadPrevious}
      labels={{
        loadMore: t('loadMore'),
        showHigher: t('showHigher'),
        jump: t('myRank.jump')
      }}
    />
  )
}
